import React, { useEffect, Component } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, MessageSquareWarningIcon, FileTextIcon, MailIcon, PhoneIcon, GlobeIcon, MapPinIcon, ArrowUpIcon } from 'lucide-react';
type Block = {
  type: 'p';
  text: string;
} | {
  type: 'list';
  items: string[];
} | {
  type: 'contact';
  heading: string;
  lines: string[];
};
interface SubSection {
  id?: string;
  title: string;
  blocks: Block[];
}
interface Section {
  id: string;
  number: string;
  title: string;
  icon: ComponentType<{
    className?: string;
  }>;
  subsections: SubSection[];
}
const sections: Section[] = [{
  id: 'privacy',
  number: '1',
  title: 'Privacy Policy',
  icon: ShieldCheckIcon,
  subsections: [{
    title: 'Our commitment to privacy',
    blocks: [{
      type: 'p',
      text: 'We respect your privacy and understand that mortgage and finance broking requires the collection of personal, financial and sometimes sensitive information. We handle personal information in accordance with the Privacy Act 1988 (Cth), the Australian Privacy Principles, applicable credit legislation, our credit representative obligations, our credit licensee\u2019s requirements, and our professional and compliance requirements.'
    }, {
      type: 'p',
      text: 'We aim to collect only the information we reasonably need to assess your finance needs, provide credit assistance, compare loan options, prepare and submit loan applications, meet legal and compliance obligations, and provide ongoing client service.'
    }]
  }, {
    title: 'What personal information we collect',
    blocks: [{
      type: 'p',
      text: 'The types of personal information we may collect include:'
    }, {
      type: 'list',
      items: ['Your name, date of birth, residential address, postal address, email address and phone number;', 'Identification information, such as driver\u2019s licence, passport, Medicare card or other identity documents;', 'Visa, residency, citizenship or employment status information where relevant to a lender assessment;', 'Family and household information, including marital status, dependants and living arrangements;', 'Employment, business, income and tax information, including payslips, tax returns, notices of assessment, financial statements, employment contracts and business records;', 'Bank account information, transaction history, living expenses and spending patterns;', 'Assets, liabilities, savings, superannuation, existing loans, credit cards, buy now pay later facilities and other credit commitments;', 'Property information, including property addresses, valuations, purchase contracts, rental income, rates notices, leases and insurance information;', 'Credit information, including credit history, credit eligibility information, repayment history, credit enquiries, defaults, hardship arrangements or other information relevant to your finance application;', 'Information about your objectives, requirements, needs, preferences, risk tolerance, future plans and financial goals;', 'Information from communications with you, including emails, text messages, phone calls, meeting notes, web forms, documents, call recordings or video verification records;', 'Referral information, including details of the person or organisation that referred you to us;', 'Complaint details, feedback and records of any dispute or concern you raise with us.']
    }, {
      type: 'p',
      text: 'We may also collect sensitive information where it is reasonably necessary for the services we provide and where permitted by law. This may include health information, criminal history, hardship information, family violence information, or other sensitive circumstances if these are relevant to assessing your finance needs, lender policy, hardship support, insurance, responsible lending, identification, or legal obligations.'
    }]
  }, {
    title: 'How we collect personal information',
    blocks: [{
      type: 'p',
      text: 'We usually collect personal information directly from you, including when you:'
    }, {
      type: 'list',
      items: ['Contact us by phone, email, SMS, social media, online form, booking link or website enquiry;', 'Speak with us during a discovery call, strategy call, appointment or review;', 'Complete a fact find, client profile, loan application, consent form or other document;', 'Upload or provide supporting documents;', 'Use our website, calculators, tools, client portal, CRM forms or online application systems;', 'Subscribe to our content, newsletters, updates or marketing.']
    }, {
      type: 'p',
      text: 'We may also collect information from third parties where permitted by law, including:'
    }, {
      type: 'list',
      items: ['Your partner, spouse, guarantor, accountant, financial adviser, conveyancer, buyer\u2019s agent, real estate agent or solicitor, where you have authorised this or where it is reasonably necessary;', 'Lenders, mortgage insurers, credit providers, aggregators, credit licensees, mentors, compliance consultants or other finance industry participants;', 'Credit reporting bodies and identity verification providers;', 'Employers, referees, property managers, valuers, insurers, government agencies, land title offices or public registers;', 'Referrers, lead providers, advertising platforms, website analytics providers and CRM systems.']
    }, {
      type: 'p',
      text: 'Where reasonable and practical, we will collect personal information directly from you. If you provide personal information about another person, you should ensure you have their consent to do so and that they are aware of this policy.'
    }]
  }, {
    title: 'Why we collect, hold, use and disclose personal information',
    blocks: [{
      type: 'p',
      text: 'We collect, hold, use and disclose personal information for purposes including:'
    }, {
      type: 'list',
      items: ['Understanding your financial position, goals, objectives and requirements;', 'Providing mortgage broking, finance broking, credit assistance and related services;', 'Assessing your borrowing capacity, loan suitability, deposit position, equity position, refinance options, investment lending options or other finance scenarios;', 'Comparing lenders, loan products, interest rates, fees, features and policy options;', 'Preparing, submitting, managing and progressing loan applications, pre-approvals, variations, refinances, pricing requests, reviews and settlement matters;', 'Verifying your identity and complying with anti-fraud, anti-money laundering, sanctions, credit and responsible lending requirements;', 'Obtaining credit reports or credit eligibility information where authorised;', 'Communicating with lenders, mortgage insurers, aggregators, credit licensees, solicitors, conveyancers, accountants, valuers, real estate agents, buyer\u2019s agents, financial advisers and other relevant parties;', 'Maintaining client records, compliance files, audit records and responsible lending evidence;', 'Providing ongoing service, reviews, repricing, refinance opportunities, annual check-ins and client support;', 'Managing complaints, disputes, feedback, hardship-related enquiries or privacy requests;', 'Improving our services, website, systems, security, training, client experience and business operations;', 'Sending education, updates, newsletters or marketing communications where permitted by law;', 'Meeting legal, regulatory, professional, audit, dispute resolution, insurance, tax and accounting obligations.']
    }, {
      type: 'p',
      text: 'We will not sell your personal information.'
    }]
  }, {
    title: 'Credit information and credit reporting',
    blocks: [{
      type: 'p',
      text: 'As part of providing credit assistance, we may collect, use and disclose credit-related information. This may include information about your current and previous loans, repayment history, credit enquiries, defaults, hardship arrangements, credit scores or information obtained from a credit reporting body.'
    }, {
      type: 'p',
      text: 'We may use credit information to assess finance options, assist with applications, verify information, comply with responsible lending obligations and communicate with lenders or other credit providers. We will only obtain a credit report where we have appropriate authority or where permitted by law.'
    }, {
      type: 'p',
      text: 'Lenders and credit providers have their own privacy and credit reporting policies. If you apply for finance, you should read the privacy and credit reporting policy of any lender or credit provider involved in your application.'
    }]
  }, {
    title: 'Who we may disclose personal information to',
    blocks: [{
      type: 'p',
      text: 'We may disclose personal information to:'
    }, {
      type: 'list',
      items: ['Banks, non-bank lenders, credit providers, mortgage managers and mortgage insurers;', 'Our credit licensee, aggregator, authorised credit representatives, mentors, compliance reviewers, auditors and professional advisers;', 'Credit reporting bodies, identity verification providers, fraud prevention services and document verification services;', 'Valuers, property inspectors, property managers, real estate agents, buyer\u2019s agents, accountants, solicitors, conveyancers, financial advisers and insurers, where relevant and authorised;', 'CRM, client portal, document collection, electronic signature, email, phone, SMS, automation, website hosting, cloud storage, analytics, cybersecurity, IT support and software providers;', 'Regulators, courts, tribunals, external dispute resolution bodies, law enforcement agencies, government agencies or other parties where required or permitted by law;', 'Any person or organisation you authorise us to deal with.']
    }, {
      type: 'p',
      text: 'We take reasonable steps to ensure third parties only receive the information reasonably necessary for their role and handle it appropriately.'
    }]
  }, {
    title: 'Overseas disclosure and cloud services',
    blocks: [{
      type: 'p',
      text: 'Some of our service providers may store, process or access information overseas, including through cloud-based systems, CRM platforms, email providers, document systems, automation tools, analytics providers or support teams. The countries involved may change depending on the service provider, but may include Australia, New Zealand, the United States, Canada, the United Kingdom, countries in the European Union, Singapore, India or the Philippines.'
    }, {
      type: 'p',
      text: 'Where we disclose personal information to an overseas recipient, we will take reasonable steps to ensure the recipient handles the information in a way that is consistent with Australian privacy requirements, unless an exception applies.'
    }]
  }, {
    title: 'Website, cookies, analytics and online forms',
    blocks: [{
      type: 'p',
      text: 'When you visit our website, we may collect technical information such as your IP address, browser type, device information, pages visited, time spent on pages, referral source and general location information. We may use cookies, pixels and analytics tools to understand website performance, improve user experience, measure advertising performance and provide relevant content.'
    }, {
      type: 'p',
      text: 'You can usually disable cookies through your browser settings, although parts of the website may not work properly if you do.'
    }, {
      type: 'p',
      text: 'If you submit information through a website form, booking link, calculator, quiz, downloadable guide or other online tool, we may use that information to respond to your enquiry, provide the requested resource, contact you about your finance needs and send relevant updates or marketing, subject to your consent and any applicable law.'
    }]
  }, {
    title: 'Artificial intelligence, automation and call recording',
    blocks: [{
      type: 'p',
      text: 'We may use technology, automation and AI-assisted tools to support administration, note-taking, call handling, appointment booking, document collection, client follow-up, marketing, workflow management and service improvement.'
    }, {
      type: 'p',
      text: 'Where phone calls, video calls or AI-assisted communications are recorded, transcribed or analysed, we will take reasonable steps to notify you and obtain any consent required by law. AI-assisted tools are used to support our business processes. We do not rely solely on automated systems to make final credit recommendations or lending decisions. Lending decisions are made by lenders, and credit assistance is provided by appropriately authorised persons.'
    }, {
      type: 'p',
      text: 'From 10 December 2026, additional privacy policy requirements apply to certain automated decisions that could reasonably be expected to significantly affect a person\u2019s rights or interests. We will review and update this policy as required to reflect any applicable automated decision-making obligations.'
    }]
  }, {
    title: 'Marketing communications',
    blocks: [{
      type: 'p',
      text: 'We may use your personal information to send you relevant educational content, loan review reminders, market updates, finance information, event invitations, newsletters or marketing communications. We may contact you by email, SMS, phone, social media, online advertising or other channels where permitted by law.'
    }, {
      type: 'p',
      text: 'You can opt out of marketing at any time by using the unsubscribe function in an email, replying STOP to an SMS where available, or contacting us at liam@scalefinancestrategies.com.au. Even if you opt out of marketing, we may still contact you about existing services, applications, compliance matters, important updates or information we are legally required to provide.'
    }]
  }, {
    title: 'Security of personal information',
    blocks: [{
      type: 'p',
      text: 'We take reasonable steps to protect personal information from misuse, interference, loss, unauthorised access, modification or disclosure. These steps may include secure document collection, password protection, multi-factor authentication, access controls, secure cloud systems, staff training, audit processes, antivirus and cybersecurity measures, and limiting access to those who need it for legitimate business purposes.'
    }, {
      type: 'p',
      text: 'No method of electronic transmission or storage is completely secure. You should take care when sending sensitive information by email or SMS. Where practical, we may ask you to use secure upload links, client portals or encrypted document systems.'
    }]
  }, {
    title: 'Retention and destruction',
    blocks: [{
      type: 'p',
      text: 'We keep personal information for as long as reasonably necessary for the purposes for which it was collected, including to provide services, manage our relationship with you, meet lender, aggregator, licensee, legal, regulatory, audit, tax, insurance and dispute resolution obligations, and maintain evidence of credit assistance provided.'
    }, {
      type: 'p',
      text: 'When information is no longer required, we will take reasonable steps to destroy it, delete it or de-identify it, unless we are required or permitted by law to retain it.'
    }]
  }, {
    title: 'Accessing or correcting your personal information',
    blocks: [{
      type: 'p',
      text: 'You may request access to the personal information we hold about you. You may also ask us to correct information you believe is inaccurate, out of date, incomplete, irrelevant or misleading.'
    }, {
      type: 'p',
      text: 'To make a request, contact:'
    }, {
      type: 'contact',
      heading: 'Privacy Officer',
      lines: ['Liam Whinfield Pty Ltd trading as Scale Finance Strategies', 'Email: liam@scalefinancestrategies.com.au', 'Phone: 0403 191 672', 'Mail: PO Box 689, Applecross WA 6153']
    }, {
      type: 'p',
      text: 'We may need to verify your identity before providing access or making corrections. We will respond within a reasonable time. In some cases, we may refuse access or correction where permitted by law, for example where providing access would unreasonably affect another person\u2019s privacy, prejudice legal proceedings, reveal commercially sensitive information, or breach a legal obligation. If we refuse a request, we will explain why, unless it would be unreasonable or unlawful to do so.'
    }]
  }, {
    title: 'Anonymity and pseudonymity',
    blocks: [{
      type: 'p',
      text: 'Where lawful and practical, you may deal with us anonymously or using a pseudonym. However, for mortgage and finance broking services, we generally need to identify you and collect accurate financial information before we can provide credit assistance, compare finance options or assist with an application.'
    }]
  }, {
    title: 'Data breaches',
    blocks: [{
      type: 'p',
      text: 'If we become aware of a data breach involving personal information, we will take steps to contain, assess and respond to the incident. If the breach is likely to result in serious harm and remedial action has not removed that risk, we will notify affected individuals and the Office of the Australian Information Commissioner where required by the Notifiable Data Breaches scheme.'
    }]
  }, {
    title: 'Privacy complaints',
    blocks: [{
      type: 'p',
      text: 'If you have a concern about how we have handled your personal information, please contact us first so we can investigate and respond.'
    }, {
      type: 'contact',
      heading: 'Privacy complaints contact',
      lines: ['Email: liam@scalefinancestrategies.com.au', 'Phone: 0403 191 672']
    }, {
      type: 'p',
      text: 'Please provide as much detail as possible about your concern, including what happened, the date, the information involved and the outcome you are seeking.'
    }, {
      type: 'p',
      text: 'We will acknowledge your complaint as soon as practicable and aim to resolve it promptly and fairly. If you are not satisfied with our response, or you do not receive a response within a reasonable time, you may contact the Office of the Australian Information Commissioner:'
    }, {
      type: 'contact',
      heading: 'Office of the Australian Information Commissioner',
      lines: ['Website: oaic.gov.au', 'Phone: 1300 363 992', 'Online privacy complaint form: available via oaic.gov.au']
    }]
  }]
}, {
  id: 'complaints',
  number: '2',
  title: 'Complaints Policy',
  icon: MessageSquareWarningIcon,
  subsections: [{
    title: 'We welcome feedback',
    blocks: [{
      type: 'p',
      text: 'We work hard to provide clear, professional and client-focused mortgage and finance broking services. If you are happy with our service, we would love to hear from you. If something has not met your expectations, we also want to hear from you so we can address it quickly and fairly.'
    }, {
      type: 'p',
      text: 'Complaints are an important part of improving our service. We do not charge you a fee for making a complaint.'
    }]
  }, {
    title: 'What is a complaint?',
    blocks: [{
      type: 'p',
      text: 'A complaint or dispute is an expression of dissatisfaction made to us, or about us, relating to our services, credit activities, representatives, conduct or complaints handling process, where a response or resolution is explicitly or implicitly expected or legally required.'
    }, {
      type: 'p',
      text: 'You do not need to use the word \u201ccomplaint\u201d for us to treat your concern as a complaint.'
    }]
  }, {
    title: 'How to make a complaint',
    blocks: [{
      type: 'p',
      text: 'You can make a complaint verbally or in writing. Complaints may be made by any reasonable means, including phone, email, letter, in person or through our website contact channels.'
    }, {
      type: 'p',
      text: 'You can contact us directly:'
    }, {
      type: 'contact',
      heading: 'Complaints Officer',
      lines: ['Liam Whinfield Pty Ltd trading as Scale Finance Strategies', 'Email: liam@scalefinancestrategies.com.au', 'Phone: 0403 191 672', 'Mail: PO Box 689, Applecross WA 6153']
    }, {
      type: 'p',
      text: 'You can also contact our credit licensee:'
    }, {
      type: 'contact',
      heading: 'Credit Licensee Complaints Contact',
      lines: ['Purple Circle Financial Services Pty Ltd', 'Email: compliance@purplecfs.com.au', 'Phone: 1300 366 406', 'Mail: Complaints Officer, Purple Circle Financial Services Pty Ltd, 14A Porter Street, Beaconsfield WA 6162']
    }, {
      type: 'p',
      text: 'When making a complaint, please provide:'
    }, {
      type: 'list',
      items: ['Your name and contact details;', 'A description of your complaint;', 'Relevant dates, documents, emails or messages;', 'The outcome you are seeking.']
    }, {
      type: 'p',
      text: 'If you make a complaint verbally, we will make a written record of the complaint. If you need help making a complaint, let us know and we will take reasonable steps to assist you. This may include helping you explain the complaint more clearly, considering interpreter support, or working with a representative, support person or advocate you authorise.'
    }]
  }, {
    title: 'How we handle complaints',
    blocks: [{
      type: 'p',
      text: 'We will handle complaints fairly, objectively, confidentially and without charge. All complaints are escalated to Purple Circle Financial Services Pty Ltd as our credit licensee so they can be recorded, assessed and managed under the applicable internal dispute resolution process.'
    }, {
      type: 'p',
      text: 'When we receive a complaint, we will:'
    }, {
      type: 'list',
      items: ['Acknowledge your complaint as soon as practicable;', 'Record and assess the complaint;', 'Investigate the issues raised;', 'Ask for more information if needed;', 'Keep you updated where appropriate;', 'Provide a response as quickly as possible;', 'Explain the reasons for our decision and any proposed resolution.']
    }, {
      type: 'p',
      text: 'Where possible, we will try to resolve your complaint immediately by providing clarification, information or a practical solution. If your complaint is resolved to your satisfaction by the end of the fifth business day after it is received, it may not need to proceed through the full internal dispute resolution process, unless a written response is required by law or you request one.'
    }, {
      type: 'p',
      text: 'If the complaint cannot be resolved quickly, we will work with Purple Circle Financial Services Pty Ltd to investigate and provide a written response within the timeframe required for the type of complaint. Depending on the complaint, the required final response timeframe may be 21, 30 or 45 calendar days.'
    }, {
      type: 'p',
      text: 'If we cannot provide a final response within the required timeframe, we will explain the reason for the delay, tell you when we expect to respond, and explain your right to take the complaint to an external dispute resolution scheme if available.'
    }, {
      type: 'p',
      text: 'When assessing complaints, we will aim to be equitable, objective and unbiased. Wherever possible, the complaint will be reviewed by a person who was not directly involved in the subject matter of the complaint. Any written response will address the issues raised and explain the reasons for the outcome.'
    }, {
      type: 'p',
      text: 'We will protect personal information about complaints and will only disclose information where needed to assess, manage or resolve the complaint, where you consent, or where required or permitted by law.'
    }]
  }, {
    title: 'Remedies',
    blocks: [{
      type: 'p',
      text: 'If a complaint is accepted and a remedy is appropriate, the remedy may be financial or non-financial. This may include an explanation, correction, apology for inconvenience or frustration, service improvement, assistance to fix an issue, or compensation for direct loss or damage where appropriate.'
    }, {
      type: 'p',
      text: 'When considering remedies, we may take into account legal obligations, relevant industry codes, fairness, good industry practice and the circumstances of the complaint.'
    }]
  }, {
    title: 'Complaints involving a lender or third party',
    blocks: [{
      type: 'p',
      text: 'If your complaint relates to a lender, insurer, conveyancer, accountant, buyer\u2019s agent, real estate agent, valuer or another third party, we may refer the complaint to that party or ask you to contact them directly. We will still consider whether any part of the complaint relates to our conduct or services.'
    }, {
      type: 'p',
      text: 'Third parties have their own complaint handling processes and external dispute resolution arrangements.'
    }]
  }, {
    title: 'External dispute resolution',
    blocks: [{
      type: 'p',
      text: 'If your complaint is not resolved to your satisfaction, or you have not received a final response within the required timeframe, you may be able to refer the complaint to the Australian Financial Complaints Authority (AFCA).'
    }, {
      type: 'p',
      text: 'AFCA provides fair and independent financial services complaint resolution that is free to consumers.'
    }, {
      type: 'contact',
      heading: 'Australian Financial Complaints Authority',
      lines: ['Website: afca.org.au', 'Phone: 1800 931 678', 'Email: info@afca.org.au', 'Mail: GPO Box 3, Melbourne VIC 3001']
    }, {
      type: 'p',
      text: 'AFCA will generally give us or the relevant financial firm an opportunity to respond to the complaint through internal dispute resolution before it progresses the matter further. If a complaint is referred to AFCA, we will cooperate with Purple Circle Financial Services Pty Ltd and AFCA to respond within the required timeframe.'
    }]
  }]
}, {
  id: 'terms',
  number: '3',
  title: 'Website Terms and Conditions',
  icon: FileTextIcon,
  subsections: [{
    title: 'Acceptance of terms',
    blocks: [{
      type: 'p',
      text: 'By accessing or using this website, you agree to these terms and conditions. If you do not agree, you should not use this website.'
    }]
  }, {
    title: 'General information only',
    blocks: [{
      type: 'p',
      text: 'The information on this website is general information only. It is provided for educational, illustrative and discussion purposes and does not take into account your personal objectives, financial situation, needs or circumstances.'
    }, {
      type: 'p',
      text: 'Before acting on any information, you should consider whether it is appropriate for your circumstances and seek professional advice where necessary.'
    }]
  }, {
    title: 'Credit assistance',
    blocks: [{
      type: 'p',
      text: 'Nothing on this website is an approval, offer, guarantee or commitment to provide finance. Any loan or credit product is subject to lender assessment, eligibility criteria, credit approval, valuation, loan terms, conditions, fees and charges.'
    }, {
      type: 'p',
      text: 'Interest rates, fees, loan features, lender policies, government schemes and eligibility requirements may change without notice. Approved applicants only.'
    }]
  }, {
    title: 'Calculators, tools and examples',
    blocks: [{
      type: 'p',
      text: 'Any calculators, tools, examples, scenarios or projections on this website are estimates only and are based on the information entered and the assumptions stated. They are not a loan approval, quote, financial forecast or substitute for personalised credit assistance.'
    }, {
      type: 'p',
      text: 'Actual outcomes may vary depending on lender policy, interest rates, valuation, income verification, expenses, credit history, tax position, property performance, market conditions and other factors.'
    }]
  }, {
    title: 'No financial, legal, tax or investment advice',
    blocks: [{
      type: 'p',
      text: 'Unless clearly stated otherwise, we provide mortgage and finance broking services. We do not provide financial planning, legal, tax, accounting, property investment, buyers agency or real estate advice.'
    }, {
      type: 'p',
      text: 'Where relevant, you should seek advice from appropriately qualified professionals, such as a financial adviser, accountant, solicitor, conveyancer, tax adviser, buyer\u2019s agent or property adviser.'
    }]
  }, {
    title: 'Accuracy and currency',
    blocks: [{
      type: 'p',
      text: 'We take reasonable care when preparing website content, but we do not guarantee that information on this website is complete, accurate, current or suitable for your intended use. Information may become outdated or may not apply to your circumstances.'
    }, {
      type: 'p',
      text: 'We may update, remove or change website content at any time without notice.'
    }]
  }, {
    title: 'Third-party links',
    blocks: [{
      type: 'p',
      text: 'This website may contain links to third-party websites. These links are provided for convenience only and do not mean we endorse the third party, their products, services, content, advice or privacy practices.'
    }, {
      type: 'p',
      text: 'Third-party websites are outside our control. You should review their terms, privacy policies and security practices before using them.'
    }]
  }, {
    title: 'Intellectual property',
    blocks: [{
      type: 'p',
      text: 'Unless otherwise stated, all content on this website, including text, graphics, logos, tools, documents, videos, downloads and layout, is owned or licensed by us and protected by copyright and other intellectual property laws.'
    }, {
      type: 'p',
      text: 'You may view, download or print content for personal, non-commercial use. You must not reproduce, adapt, publish, distribute, sell, modify or use our content for commercial purposes without our written permission.'
    }]
  }, {
    title: 'Limitation of liability',
    blocks: [{
      type: 'p',
      text: 'To the maximum extent permitted by law, we are not liable for any loss, damage, cost or expense, whether direct or indirect, arising from your use of this website, reliance on website content, use of third-party links, technical issues, website interruption, errors, omissions or unauthorised access.'
    }, {
      type: 'p',
      text: 'Nothing in these terms excludes, restricts or modifies any consumer guarantee, right or remedy that cannot be excluded under Australian law.'
    }]
  }, {
    title: 'Governing law',
    blocks: [{
      type: 'p',
      text: 'These terms are governed by the laws of Western Australia and the Commonwealth of Australia. This website is intended for use by people in Australia. It may not comply with the laws of other countries.'
    }]
  }]
}];
const licenceDetails: {
  label: string;
  value: string;
}[] = [{
  label: 'Licence status',
  value: 'Corporate Credit Representative of Purple Circle Financial Services Pty Ltd, Australian Credit Licence 486112. Liam Whinfield is a personal Credit Representative.'
}, {
  label: 'Personal credit representative number',
  value: '579007'
}, {
  label: 'Corporate credit representative number',
  value: '579006'
}, {
  label: 'ABN',
  value: '40 654 344 260'
}, {
  label: 'ACN',
  value: '654 344 260'
}, {
  label: 'Licensee',
  value: 'Purple Circle Financial Services Pty Ltd, Australian Credit Licence 486112'
}, {
  label: 'AFCA membership',
  value: 'Purple Circle Financial Services Pty Ltd is a member of the Australian Financial Complaints Authority.'
}];
function BlockRenderer({
  block
}: {
  block: Block;
}) {
  if (block.type === 'p') {
    return <p className="text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-191">
        {block.text}
      </p>;
  }
  if (block.type === 'list') {
    return <ul className="space-y-2 pl-1" data-id="element-192">
        {block.items.map((item, i) => <li key={i} className="flex gap-3 text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-193">
            <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500" data-id="element-194" />
            <span data-id="element-195">{item}</span>
          </li>)}
      </ul>;
  }
  // contact
  return <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-5" data-id="element-196">
      <p className="font-semibold text-slate-900 dark:text-white mb-2" data-id="element-197">
        {block.heading}
      </p>
      <div className="space-y-1" data-id="element-198">
        {block.lines.map((line, i) => <p key={i} className="text-sm text-slate-600 dark:text-slate-400" data-id="element-199">
            {line}
          </p>)}
      </div>
    </div>;
}
export function LegalPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="pt-28 pb-24 bg-[#f8f9fb] dark:bg-[#0f1629] min-h-screen transition-colors duration-300" data-id="element-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-201">
        {/* Header */}
        <div className="max-w-3xl" data-id="element-202">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none mb-6" data-id="element-203">
            <span className="text-sm font-medium text-primary-600 dark:text-primary-400" data-id="element-204">
              Legal
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4" data-id="element-205">
            Privacy Policy, Complaints Policy and Website Terms
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 leading-relaxed" data-id="element-206">
            Liam Whinfield Pty Ltd trading as Scale Finance Strategies — a
            mortgage and finance broking business. This page explains how we
            handle your personal information, how you can make a complaint, and
            the terms that apply when you use our website.
          </p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-4" data-id="element-207">
            Last updated: 1 July 2026
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-12" data-id="element-208">
          {/* Table of contents */}
          <aside className="lg:sticky lg:top-28 h-max" data-id="element-209">
            <nav aria-label="On this page" className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#1a2035] p-5 shadow-sm dark:shadow-none" data-id="element-210">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-4" data-id="element-211">
                On this page
              </p>
              <ul className="space-y-2" data-id="element-212">
                {sections.map(section => {
                const Icon = section.icon;
                return <li key={section.id} data-id="element-213">
                      <a href={`#${section.id}`} className="flex items-center gap-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors" data-id="element-214">
                        <Icon className="w-4 h-4 flex-shrink-0 text-primary-500" data-id="element-215" />
                        {section.title}
                      </a>
                    </li>;
              })}
                <li data-id="element-216">
                  <a href="#contact-us" className="flex items-center gap-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors" data-id="element-217">
                    <MailIcon className="w-4 h-4 flex-shrink-0 text-primary-500" data-id="element-218" />
                    Contact us
                  </a>
                </li>
              </ul>
            </nav>
          </aside>

          {/* Content */}
          <div className="min-w-0" data-id="element-219">
            {/* Licence details */}
            <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#1a2035] p-6 md:p-8 shadow-sm dark:shadow-none mb-12" data-id="element-220">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-5" data-id="element-221">
                Business &amp; licence details
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-id="element-222">
                {licenceDetails.map(detail => <div key={detail.label} data-id="element-223">
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1" data-id="element-224">
                      {detail.label}
                    </dt>
                    <dd className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed" data-id="element-225">
                      {detail.value}
                    </dd>
                  </div>)}
              </dl>
            </div>

            {sections.map(section => {
            const Icon = section.icon;
            return <section key={section.id} id={section.id} className="scroll-mt-28 mb-16" data-id="element-226">
                  <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-200 dark:border-white/10" data-id="element-227">
                    <div className="p-2.5 rounded-xl bg-primary-100 dark:bg-primary-500/15 text-primary-600 dark:text-primary-400" data-id="element-228">
                      <Icon className="w-5 h-5" data-id="element-229" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-230">
                      {section.number}. {section.title}
                    </h2>
                  </div>

                  <div className="space-y-10" data-id="element-231">
                    {section.subsections.map((sub, si) => <div key={si} data-id="element-232">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4" data-id="element-233">
                          {section.number}.{si + 1} {sub.title}
                        </h3>
                        <div className="space-y-4" data-id="element-234">
                          {sub.blocks.map((block, bi) => <BlockRenderer key={bi} block={block} data-id="element-235" />)}
                        </div>
                      </div>)}
                  </div>
                </section>;
          })}

            {/* Contact us */}
            <section id="contact-us" className="scroll-mt-28" data-id="element-236">
              <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-200 dark:border-white/10" data-id="element-237">
                <div className="p-2.5 rounded-xl bg-primary-100 dark:bg-primary-500/15 text-primary-600 dark:text-primary-400" data-id="element-238">
                  <MailIcon className="w-5 h-5" data-id="element-239" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-240">
                  4. Contact us
                </h2>
              </div>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6" data-id="element-241">
                For privacy questions, complaints, access or correction
                requests, website questions or general enquiries, contact:
              </p>
              <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#1a2035] p-6 md:p-8 shadow-sm dark:shadow-none" data-id="element-242">
                <p className="font-semibold text-slate-900 dark:text-white mb-4" data-id="element-243">
                  Liam Whinfield Pty Ltd trading as Scale Finance Strategies
                </p>
                <div className="space-y-3" data-id="element-244">
                  <a href="mailto:liam@scalefinancestrategies.com.au" className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors" data-id="element-245">
                    <MailIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-246" />
                    liam@scalefinancestrategies.com.au
                  </a>
                  <a href="tel:0403191672" className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors" data-id="element-247">
                    <PhoneIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-248" />
                    0403 191 672
                  </a>
                  <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400" data-id="element-249">
                    <GlobeIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-250" />
                    scalefinancestrategies.com.au
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400" data-id="element-251">
                    <MapPinIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-252" />
                    PO Box 689, Applecross WA 6153
                  </div>
                </div>
              </div>
            </section>

            <div className="mt-12 flex items-center justify-between gap-4" data-id="element-253">
              <Link to="/" className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline" data-id="element-254">
                ← Back to home
              </Link>
              <button type="button" onClick={() => window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors" data-id="element-255">
                Back to top
                <ArrowUpIcon className="w-4 h-4" data-id="element-256" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>;
}